import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CircleAlert, Loader2, RotateCw } from 'lucide-react';

import { AdminLayout, adminToolPath } from '../../components/AdminLayout';
import { useViewModelReady } from '../../hooks/useViewModel';
import type { GoogleNexusUpload } from '../../domain/nexus';
import {
  diagnosticMessage,
  formatNexusBytes,
  formatNexusTimestamp,
  summarizeNexusValidationResponse,
} from '../AdminPage/tools/GoogleNexusTool/helpers';
import { CopyOnClick } from '../AdminPage/tools/GoogleNexusTool/CopyOnClick';
import { NexusStatusBadge } from '../AdminPage/tools/GoogleNexusTool/StatusBadge';
import {
  AdminNexusUploadDetailViewModel,
  NEXUS_UPLOAD_POLL_INTERVAL_MS,
  useAdminNexusUploadDetailViewModel,
} from './viewModel';

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

export function AdminNexusUploadDetailPage() {
  const { uploadId } = useParams<{ uploadId: string }>();
  const navigate = useNavigate();
  const vm = useAdminNexusUploadDetailViewModel(uploadId ?? '');
  useViewModelReady(vm.initialize);

  if (!uploadId) {
    return <Navigate to={adminToolPath('googleNexus')} replace />;
  }

  return (
    <AdminLayout
      activeId="googleNexus"
      onSelectTool={(id) => navigate(adminToolPath(id))}
      backLabel="Back to admin"
      onBack={() => navigate(adminToolPath('googleNexus'))}
    >
      {({ mobileNavTrigger }) => (
        <DetailContent vm={vm} mobileNavTrigger={mobileNavTrigger} />
      )}
    </AdminLayout>
  );
}

function DetailContent({
  vm,
  mobileNavTrigger,
}: {
  vm: AdminNexusUploadDetailViewModel;
  mobileNavTrigger?: React.ReactNode;
}) {
  const backLink = (
    <Link
      to={adminToolPath('googleNexus')}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Google Nexus
    </Link>
  );

  if (vm.busy) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { upload } = vm;

  return (
    <section className="min-w-0 max-w-7xl">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {backLink}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">
              {upload?.displayName ?? 'Validation upload'}
            </h1>
            {upload && <NexusStatusBadge status={upload.status} />}
            {vm.polling && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                <RotateCw className="h-3 w-3 animate-spin" />
                polling every {NEXUS_UPLOAD_POLL_INTERVAL_MS / 1000}s
              </span>
            )}
          </div>
          {upload && (
            <p className="mt-1 font-mono text-xs text-gray-500">{upload.remoteProjectId || upload.id}</p>
          )}
        </div>
        {mobileNavTrigger}
      </div>

      {(vm.error || vm.pollError) && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{vm.error || `Poll: ${vm.pollError}`}</span>
        </div>
      )}

      {upload && <UploadDetail upload={upload} />}
    </section>
  );
}

function UploadDetail({ upload }: { upload: GoogleNexusUpload }) {
  const summary = summarizeNexusValidationResponse(upload.remoteResponse);
  const failure = upload.error || upload.lastPollError;
  return (
    <>
      <dl className="mt-6 grid gap-3 rounded-md border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2 sm:p-5 lg:grid-cols-4">
        <Field label="Format">
          {upload.validationFormat === 'harbor'
            ? 'Harbor'
            : upload.validationFormat === 'dockerWorlds'
              ? 'Docker Worlds'
              : '—'}
          {upload.testRun && <span className="ml-2 text-xs text-blue-600">test run</span>}
        </Field>
        <Field label="Archive">
          <span className="block truncate">{upload.archiveName}</span>
          <span className="text-xs text-gray-400">{formatNexusBytes(upload.archiveSizeBytes)}</span>
        </Field>
        <Field label="Created">{formatNexusTimestamp(upload.createdAt)}</Field>
        <Field label="Last polled">{formatNexusTimestamp(upload.lastPolledAt)}</Field>
        {upload.completedAt && <Field label="Completed">{formatNexusTimestamp(upload.completedAt)}</Field>}
        {upload.latestDeliveryCollectionId && (
          <Field label="Delivery collection">
            <CopyOnClick
              value={upload.latestDeliveryCollectionId}
              label="delivery collection"
              className="break-all font-mono text-xs"
            >
              {upload.latestDeliveryCollectionId}
            </CopyOnClick>
          </Field>
        )}
        {summary && summary.total > 0 && (
          <Field label="Results">
            {summary.passed} passed · {summary.failed} failed
            {summary.errored ? ` · ${summary.errored} errors` : ''}
            {summary.skipped ? ` · ${summary.skipped} skipped` : ''}
          </Field>
        )}
      </dl>

      {Boolean(failure) && (
        <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {diagnosticMessage(failure)}
        </p>
      )}

      <JsonPanel title="Output" value={upload.remoteResponse ?? null} />
      {Boolean(failure) && <JsonPanel title="Error" value={failure} />}
      <JsonPanel title="Request" value={upload.requestSummary ?? null} />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-gray-900">{children}</dd>
    </div>
  );
}

function JsonPanel({ title, value }: { title: string; value: unknown }) {
  return (
    <section className="mt-6 overflow-hidden rounded-md border border-gray-700 bg-gray-950">
      <h2 className="border-b border-gray-700 px-4 py-3 text-sm font-medium text-gray-100">{title}</h2>
      <pre className="max-h-[40rem] overflow-auto p-4 text-xs leading-relaxed text-gray-100">
        {value === null ? 'No output yet.' : prettyJson(value)}
      </pre>
    </section>
  );
}
